import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuthStore } from '../lib/auth'

export default function Login() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle)
  const resendVerification = useAuthStore((s) => s.resendVerification)

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleGoogleSignIn = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      await signInWithGoogle()
      // Browser will redirect to Google — no further action needed
    } catch (err) {
      setError(err.message || 'Google sign-in failed. Please try again.')
      setGoogleLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await login(formData.email, formData.password)

      // Check for pending scan (teaser flow)
      const pendingScan = localStorage.getItem('pending_scan')
      if (pendingScan) {
        try {
          const scanState = JSON.parse(pendingScan)
          localStorage.removeItem('pending_scan')
          navigate('/report', { state: scanState })
          return
        } catch (e) {
          console.error('Parse error:', e)
        }
      }

      navigate('/dashboard')
    } catch (err) {
      console.error('Login error:', err)

      if (err.message.includes('not verified') || err.message.includes('not confirmed')) {
        setError('Email not verified. Please check your inbox for the verification link.')
      } else if (err.message.includes('Invalid')) {
        setError('Invalid email or password')
      } else {
        setError(err.message || 'Login failed. Please try again.')
      }

      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    try {
      await resendVerification(formData.email)
      alert('Verification email sent! Check your inbox.')
    } catch (err) {
      setError('Failed to resend verification email: ' + err.message)
    }
  }

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      <Header />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="card max-w-md w-full">
          <h2 className="text-3xl font-bold mb-2 text-center" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            Welcome Back
          </h2>
          <p className="text-gray-400 text-center mb-6">
            Sign in to your ScamView account
          </p>

          {error && (
            <div className="bg-red-500/20 text-red-400 p-3 rounded-lg mb-4 text-center border border-red-500/30">
              {error}
              {(error.includes('not verified') || error.includes('not confirmed')) && formData.email && (
                <button
                  onClick={handleResendVerification}
                  className="block mx-auto mt-2 text-sm underline hover:text-white transition"
                >
                  Resend Verification Link
                </button>
              )}
            </div>
          )}

          {/* Google sign-in */}
          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-medium py-3 px-4 rounded-lg hover:bg-gray-100 transition disabled:opacity-60 disabled:cursor-not-allowed mb-4"
          >
            {googleLoading ? (
              <svg className="w-5 h-5 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            {googleLoading ? 'Redirecting…' : 'Continue with Google'}
          </button>

          <div className="relative flex items-center gap-3 mb-4">
            <div className="flex-1 border-t border-white/10" />
            <span className="text-xs text-gray-500">or sign in with email</span>
            <div className="flex-1 border-t border-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-400 mb-2 text-sm">Email</label>
              <input
                type="email"
                name="email"
                className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-gray-400 mb-2 text-sm">Password</label>
              <input
                type="password"
                name="password"
                className="w-full bg-gray-900/50 border border-white/20 rounded-lg p-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter your password"
                autoComplete="current-password"
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-cyan-400 hover:text-cyan-300 transition"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              Don't have an account?{' '}
              <Link to="/plans" className="text-cyan-400 hover:text-cyan-300 transition font-semibold">
                Sign up
              </Link>
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-gray-500 text-xs">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-gray-400 hover:text-white transition underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-gray-400 hover:text-white transition underline">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
